/**
 * Vikunja import: the wire shapes the api proxy and the browser agree on, and
 * the pure field mapping between a Vikunja task and one of ours.
 *
 * Everything here is pure so it can be unit tested with no services and no
 * browser. The api does the fetching (Vikunja sends no CORS headers, so the
 * browser cannot call it) and the browser does the writing (it is already the
 * bulk Yjs writer, and one chunked transaction beats one projection per task).
 *
 * Targets API v2 (/api/v2, OpenAPI 3.1) with `?format=markdown`, so
 * descriptions arrive as GFM Markdown and need no HTML conversion. v1 is
 * deprecated in Vikunja 3.0 and gone in 4.0.
 */
import { MAX_TASK_DESCRIPTION, MAX_TASK_TITLE } from "./tasks.js";
import { describeRecurrence, parseRecurrence } from "./recurrence.js";
/** Path prefix for every request the importer makes. */
export const VIKUNJA_API_PREFIX = "/api/v2";
/** Ceiling on one import run, so a huge instance cannot run away with a doc. */
export const VIKUNJA_MAX_TASKS = 5000;
/** Requested page size. The instance clamps this to its max_items_per_page. */
export const VIKUNJA_PAGE_SIZE = 250;
// ---------- dates ----------
/**
 * Go's zero time. Vikunja serializes every unset timestamp as this, so a
 * missing check here gives every task a due date in the year 1.
 */
export function isVikunjaUnset(ts) {
    if (!ts)
        return true;
    if (ts.startsWith("0001-01-01"))
        return true;
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) || new Date(parsed).getUTCFullYear() < 1970;
}
function pad(n) {
    return String(n).padStart(2, "0");
}
/** Epoch ms for a Vikunja timestamp, or undefined when it is unset. */
export function vikunjaTimestamp(ts) {
    if (isVikunjaUnset(ts))
        return undefined;
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? undefined : parsed;
}
/**
 * Vikunja due dates are instants; ours are a local date plus an optional local
 * time. Convert in the caller's zone and drop a midnight time, which is how
 * Vikunja stores a date-only due date.
 */
export function vikunjaDueParts(ts) {
    const ms = vikunjaTimestamp(ts);
    if (ms === undefined)
        return {};
    const d = new Date(ms);
    const dueDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const h = d.getHours();
    const min = d.getMinutes();
    if (h === 0 && min === 0)
        return { dueDate };
    return { dueDate, dueTime: `${pad(h)}:${pad(min)}` };
}
/** Local date as "YYYY-MM-DD", for footer lines. */
export function vikunjaLocalDate(ts) {
    return vikunjaDueParts(ts).dueDate;
}
// ---------- recurrence ----------
const SECONDS_PER_DAY = 86_400;
const SECONDS_PER_WEEK = 604_800;
/**
 * Map Vikunja's repeat settings onto our rule.
 *
 * repeat_mode: 0 repeats repeat_after seconds after the due date, 1 repeats
 * monthly, 2 repeats repeat_after seconds from the completion date. We model
 * "how often", not "measured from what", so 0 and 2 both come down to the
 * interval. Vikunja has no weekday sets, so nothing maps onto ours.
 *
 * Returns null when there is nothing to map or the interval is out of range
 * (parseRecurrence caps at 365); the caller notes that in the footer rather
 * than silently dropping it.
 */
export function mapVikunjaRecurrence(repeatAfter, repeatMode) {
    if (repeatMode === 1)
        return parseRecurrence({ unit: "month", interval: 1 });
    const seconds = Math.floor(repeatAfter ?? 0);
    if (!Number.isFinite(seconds) || seconds <= 0)
        return null;
    if (seconds % SECONDS_PER_WEEK === 0) {
        return parseRecurrence({ unit: "week", interval: seconds / SECONDS_PER_WEEK });
    }
    if (seconds % SECONDS_PER_DAY === 0) {
        return parseRecurrence({ unit: "day", interval: seconds / SECONDS_PER_DAY });
    }
    // sub-day or ragged intervals: round up to whole days, the finest thing a
    // date-only due date can express
    return parseRecurrence({ unit: "day", interval: Math.max(1, Math.ceil(seconds / SECONDS_PER_DAY)) });
}
// ---------- footer ----------
export const VIKUNJA_PRIORITIES = {
    1: "low",
    2: "medium",
    3: "high",
    4: "urgent",
    5: "do now",
};
/**
 * A compact provenance block for everything Hydra Tasks does not model, so an
 * import loses nothing silently. Only lines whose field is actually set are
 * emitted; an all-empty footer returns "".
 */
export function buildVikunjaFooter(raw, ctx) {
    const lines = [];
    if (ctx.projectTitle)
        lines.push(`Project: ${ctx.projectTitle}`);
    const priority = VIKUNJA_PRIORITIES[raw.priority ?? 0];
    if (priority)
        lines.push(`Priority: ${priority}`);
    const labels = (raw.labels ?? []).map((l) => l.title).filter((t) => !!t);
    if (labels.length > 0)
        lines.push(`Labels: ${labels.join(", ")}`);
    const assignees = (raw.assignees ?? [])
        .map((a) => a.name?.trim() || a.username)
        .filter((n) => !!n);
    if (assignees.length > 0)
        lines.push(`Assignees: ${assignees.join(", ")}`);
    const starts = vikunjaLocalDate(raw.start_date);
    if (starts)
        lines.push(`Starts: ${starts}`);
    const ends = vikunjaLocalDate(raw.end_date);
    if (ends)
        lines.push(`Ends: ${ends}`);
    const percent = raw.percent_done ?? 0;
    if (percent > 0 && percent < 1)
        lines.push(`Progress: ${Math.round(percent * 100)} percent`);
    const parents = raw.related_tasks?.["parenttask"] ?? [];
    const parent = parents.find((p) => !!p?.title);
    if (parent)
        lines.push(`Subtask of: ${parent.title}`);
    const subtasks = raw.related_tasks?.["subtask"] ?? [];
    if (subtasks.length > 0)
        lines.push(`Subtasks: ${subtasks.length} (imported as separate tasks)`);
    if (ctx.unmappedRepeat)
        lines.push("Repeats: schedule could not be imported, set it here");
    if (ctx.completedRepeat)
        lines.push(`Repeated: ${ctx.completedRepeat} (not carried over, this one is finished)`);
    lines.push(`Source: ${ctx.baseUrl}/tasks/${raw.id}`);
    return lines.join("\n");
}
// ---------- task mapping ----------
/** Between the imported body and the provenance block. */
const FOOTER_SEPARATOR = "\n\n---\n";
/** Provenance key. The host keeps two instances from colliding. */
export function vikunjaSourceId(host, taskId) {
    return `vikunja:${host}:${taskId}`;
}
/**
 * One Vikunja task to one import entry. The description body is truncated
 * before the footer is appended, so provenance survives a very long body.
 */
export function mapVikunjaTask(raw, ctx) {
    const rule = mapVikunjaRecurrence(raw.repeat_after, raw.repeat_mode);
    const repeats = (raw.repeat_after ?? 0) > 0 || raw.repeat_mode === 1;
    /**
     * A task that arrives ALREADY completed must not carry a schedule. The sync
     * respawn engine reads "done with no prior row" as a completion transition
     * (which is what makes an offline completion work), so importing completed
     * repeating history would spawn a successor per task, inventing tasks that
     * do not exist at the source. Vikunja reschedules the same task on
     * completion anyway, so a done repeating task there is finished for good.
     */
    const recurrence = raw.done === true ? null : rule;
    const footer = ctx.footer
        ? buildVikunjaFooter(raw, {
            baseUrl: ctx.baseUrl,
            projectTitle: ctx.projectTitle,
            unmappedRepeat: repeats && !rule,
            ...(raw.done === true && rule ? { completedRepeat: describeRecurrence(rule) } : {}),
        })
        : "";
    const body = (raw.description ?? "").trim();
    let description;
    if (footer) {
        // budget the separator exactly: one character out and the outer cap below
        // would shave the end off the footer, losing the Source link
        const room = MAX_TASK_DESCRIPTION - footer.length - FOOTER_SEPARATOR.length;
        if (room <= 0) {
            description = footer;
        }
        else {
            const trimmed = body.length > room ? `${body.slice(0, Math.max(0, room - 3))}...` : body;
            description = trimmed ? `${trimmed}${FOOTER_SEPARATOR}${footer}` : footer;
        }
    }
    else if (body) {
        description = body.slice(0, MAX_TASK_DESCRIPTION);
    }
    const title = (raw.title ?? "").trim() || "Untitled task";
    const entry = {
        sourceId: vikunjaSourceId(ctx.host, raw.id),
        title: title.slice(0, MAX_TASK_TITLE),
    };
    if (description)
        entry.description = description.slice(0, MAX_TASK_DESCRIPTION);
    const due = vikunjaDueParts(raw.due_date);
    if (due.dueDate)
        entry.dueDate = due.dueDate;
    if (due.dueTime)
        entry.dueTime = due.dueTime;
    if (raw.done === true) {
        entry.done = true;
        const doneAt = vikunjaTimestamp(raw.done_at);
        if (doneAt !== undefined)
            entry.doneAt = doneAt;
    }
    if (recurrence)
        entry.recurrence = recurrence;
    return entry;
}
//# sourceMappingURL=vikunja.js.map